import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createImagesURLs } from "../Functions/ImageFunctions";
import { getMultipleProductsUrl } from "../../services/File/File";
import {
  deleteProductImage,
  getAllProductImage,
  updateProductImage,
} from "../../services/ProductsImages/ProductsImages";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";
import PhotoDialog from "./AdminDialog/PhotoDialog";

interface ProductImageRow {
  _id: string;
  key: string;
  filename: string;
  category: number;
  url?: string;
}

const AdminProductImages = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [rows, setRows] = useState<ProductImageRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [showAddPhoto, setShowAddPhoto] = useState<boolean>(false);
  const [numOfFiles, setNumOfFiles] = useState<number>(0);
  const [images, setImages] = useState<any[]>([]);
  const [imagesUrls, setImagesUrls] = useState<any[]>([]);

  const [editingRow, setEditingRow] = useState<ProductImageRow | null>(null);
  const [deletingRow, setDeletingRow] = useState<ProductImageRow | null>(null);

  const loadImages = () => {
    setLoading(true);
    getAllProductImage()
      .then((res: any) => {
        const items: ProductImageRow[] = res.data ?? [];
        getMultipleProductsUrl(items)
          .then((withUrls: any[]) => {
            setRows(
              items.map((item, i) => ({ ...item, url: withUrls[i]?.url }))
            );
          })
          .catch(() => setRows(items));
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadImages();
  }, []);

  useEffect(() => {
    createImagesURLs(images, setImagesUrls, imagesUrls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  // The upload dialog creates ProductImages rows itself; reload the list once it's closed.
  useEffect(() => {
    if (!showAddPhoto) {
      loadImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddPhoto]);

  const submitEdit = () => {
    if (!editingRow) return;
    updateProductImage({ _id: editingRow._id, filename: editingRow.filename })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingRow(null);
        loadImages();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingRow) return;
    deleteProductImage({ _id: deletingRow._id })
      .then(() => {
        setRows((prev) => prev.filter((r) => r._id !== deletingRow._id));
        setDeletingRow(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader
        title={t("common:images")}
        action={
          <Button className="general-button" onClick={() => setShowAddPhoto(true)}>
            {t("common:productPhoto")}
          </Button>
        }
      />

      <div className="admin-page-body">
      <DataTable value={rows} loading={loading} paginator rows={20} dataKey="_id" emptyMessage={t("common:noResults")}>
        <Column
          header=""
          body={(row: ProductImageRow) =>
            row.url ? (
              <img src={row.url} alt={row.filename} style={{ width: "60px", height: "60px", objectFit: "cover" }} />
            ) : null
          }
        />
        <Column field="filename" header={t("common:Name")} sortable />
        <Column
          body={(row: ProductImageRow) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingRow({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingRow(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <PhotoDialog
        images={images}
        setImages={setImages}
        imagesUrls={imagesUrls}
        setImagesUrls={setImagesUrls}
        numOfFiles={numOfFiles}
        setNumOfFiles={setNumOfFiles}
        showAddPhoto={showAddPhoto}
        setShowAddPhoto={setShowAddPhoto}
        toast={toast}
      />

      <Dialog
        header={t("common:Name")}
        visible={editingRow != null}
        onHide={() => setEditingRow(null)}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingRow(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingRow && (
          <InputText
            style={{ width: "100%" }}
            value={editingRow.filename}
            onChange={(e) => setEditingRow({ ...editingRow, filename: e.target.value })}
          />
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingRow(null)}
        visible={deletingRow != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingRow(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingRow?.filename}
      </Dialog>
    </div>
  );
};

export default AdminProductImages;
